import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  ArchiveBoxIcon,
  TrashIcon,
  TagIcon,
  PlusIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

interface ConversationItem {
  id: string;
  title: string;
  messageCount: number;
  lastMessageAt: string;
  archived: boolean;
  label?: string;
  labelEmoji?: string;
  companion?: { name: string; avatarUrl?: string };
}

interface ConversationDashboardProps {
  isOpen: boolean;
  onClose: () => void;
  companionId: string;
  currentConversationId?: string;
  onSelectConversation: (id: string) => void;
  onNewConversation: () => void;
}

const LABELS = [
  { emoji: '🌙', name: 'Creative' },
  { emoji: '🔮', name: 'Tarot' },
  { emoji: '📖', name: 'Story' },
  { emoji: '💜', name: 'Personal' },
  { emoji: '🧠', name: 'Research' },
  { emoji: '✍️', name: 'Writing' },
  { emoji: '🎭', name: 'Roleplay' },
  { emoji: '💡', name: 'Ideas' },
];

const ConversationDashboard: React.FC<ConversationDashboardProps> = ({
  isOpen,
  onClose,
  companionId,
  currentConversationId,
  onSelectConversation,
  onNewConversation,
}) => {
  const { t } = useTranslation();
  const [conversations, setConversations] = useState<ConversationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [filterLabel, setFilterLabel] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchMode, setBatchMode] = useState(false);
  const [labelingId, setLabelingId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'recent' | 'name' | 'messages'>('recent');

  useEffect(() => {
    if (isOpen) fetchConversations();
  }, [isOpen, showArchived, filterLabel, sortBy]);

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = { companionId, sort: sortBy };
      if (showArchived) params.archived = 'true';
      if (filterLabel) params.label = filterLabel;
      if (search) params.search = search;
      const res = await axios.get('/chat/conversations', { params });
      setConversations(res.data);
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => fetchConversations();

  const handleArchive = async (id: string) => {
    try {
      await axios.patch(`/chat/conversations/${id}/archive`);
      toast.success(
        showArchived
          ? t('conversations.unarchived', 'Unarchived')
          : t('conversations.archived', 'Archived'),
      );
      fetchConversations();
    } catch (err) {
      toast.error('Failed');
    }
  };

  const handleSetLabel = async (id: string, label: string, emoji: string) => {
    try {
      await axios.patch(`/chat/conversations/${id}/label`, {
        label,
        labelEmoji: emoji,
      });
      setLabelingId(null);
      fetchConversations();
    } catch (err) {
      toast.error('Failed');
    }
  };

  const handleClearLabel = async (id: string) => {
    try {
      await axios.patch(`/chat/conversations/${id}/label`, {
        label: null,
        labelEmoji: null,
      });
      fetchConversations();
    } catch (err) {
      toast.error('Failed');
    }
  };

  const handleBatchArchive = async () => {
    try {
      await axios.post('/chat/conversations/batch/archive', {
        conversationIds: [...selectedIds],
      });
      toast.success(t('conversations.batchArchived', 'Archived'));
      setSelectedIds(new Set());
      setBatchMode(false);
      fetchConversations();
    } catch (err) {
      toast.error('Failed');
    }
  };

  const handleBatchDelete = async () => {
    if (
      !window.confirm(
        t(
          'conversations.batchDeleteConfirm',
          'Delete selected conversations? This cannot be undone.',
        ),
      )
    )
      return;
    try {
      await axios.post('/chat/conversations/batch/delete', {
        conversationIds: [...selectedIds],
      });
      toast.success(t('conversations.batchDeleted', 'Deleted'));
      setSelectedIds(new Set());
      setBatchMode(false);
      fetchConversations();
    } catch (err) {
      toast.error('Failed');
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 1) return t('conversations.yesterday', 'Yesterday');
    if (diffDays < 7) return d.toLocaleDateString([], { weekday: 'short' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (!isOpen) return null;

  const usedLabels = [...new Set(conversations.map((c) => c.label).filter(Boolean))];

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed left-0 top-0 h-full w-96 max-w-[90vw] bg-dark-bg dark:bg-dark-bg bg-light-bg z-50 shadow-2xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-dark-border dark:border-dark-border border-light-border">
          <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text">
            💬 {t('conversations.title', 'Conversations')}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                onNewConversation();
                onClose();
              }}
              className="p-1.5 rounded-full hover:bg-dark-card text-primary-400"
            >
              <PlusIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => {
                setBatchMode(!batchMode);
                setSelectedIds(new Set());
              }}
              className={`text-xs px-2 py-1 rounded transition-colors ${
                batchMode
                  ? 'bg-primary-600 text-white'
                  : 'text-dark-text-secondary hover:bg-dark-card'
              }`}
            >
              {batchMode ? t('common.done', 'Done') : t('conversations.select', 'Select')}
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-full hover:bg-dark-card transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-4 py-2 border-b border-dark-border/30">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-2.5 text-dark-text-secondary" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder={t('conversations.searchPlaceholder', 'Search conversations...')}
                className="w-full pl-9 pr-3 py-2 rounded-lg bg-dark-card/50 text-sm text-dark-text border border-dark-border/30 focus:border-primary-500 focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="px-4 py-2 border-b border-dark-border/30 flex gap-2 flex-wrap">
          <button
            onClick={() => setShowArchived(!showArchived)}
            className={`text-xs px-2 py-1 rounded-full transition-colors ${
              showArchived
                ? 'bg-primary-600 text-white'
                : 'bg-dark-card/50 text-dark-text-secondary hover:bg-dark-card'
            }`}
          >
            📦 {t('conversations.archived', 'Archived')}
          </button>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'recent' | 'name' | 'messages')}
            className="text-xs px-2 py-1 rounded bg-dark-card/50 text-dark-text-secondary border border-dark-border/30 focus:outline-none"
          >
            <option value="recent">{t('conversations.sortRecent', 'Recent')}</option>
            <option value="name">{t('conversations.sortName', 'Name')}</option>
            <option value="messages">{t('conversations.sortMessages', 'Most messages')}</option>
          </select>
          {usedLabels.map((label) => (
            <button
              key={label}
              onClick={() => setFilterLabel(filterLabel === label ? null : label!)}
              className={`text-xs px-2 py-1 rounded-full transition-colors ${
                filterLabel === label
                  ? 'bg-primary-600 text-white'
                  : 'bg-dark-card/50 text-dark-text-secondary hover:bg-dark-card'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Batch Actions */}
        {batchMode && selectedIds.size > 0 && (
          <div className="px-4 py-2 border-b border-primary-500/20 bg-primary-900/10 flex items-center gap-2">
            <span className="text-xs text-primary-400">
              {selectedIds.size} {t('conversations.selected', 'selected')}
            </span>
            <div className="flex-1" />
            <button
              onClick={handleBatchArchive}
              className="text-xs px-2 py-1 rounded bg-dark-card/50 text-dark-text-secondary hover:bg-dark-card"
            >
              📦
            </button>
            <button
              onClick={handleBatchDelete}
              className="text-xs px-2 py-1 rounded bg-red-600/20 text-red-400 hover:bg-red-600/30"
            >
              🗑️
            </button>
          </div>
        )}

        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : conversations.length === 0 ? (
            <div className="text-center py-8 text-dark-text-secondary">
              <ChatBubbleLeftRightIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">{t('conversations.empty', 'No conversations yet')}</p>
            </div>
          ) : (
            conversations.map((conv) => (
              <div
                key={conv.id}
                className={`flex items-center gap-3 px-4 py-3 border-b border-dark-border/10 hover:bg-dark-card/30 transition-colors cursor-pointer ${
                  conv.id === currentConversationId
                    ? 'bg-primary-900/20 border-l-2 border-l-primary-500'
                    : ''
                }`}
                onClick={() => {
                  if (!batchMode) {
                    onSelectConversation(conv.id);
                    onClose();
                  }
                }}
              >
                {batchMode && (
                  <input
                    type="checkbox"
                    checked={selectedIds.has(conv.id)}
                    onChange={() => {
                      setSelectedIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(conv.id)) next.delete(conv.id);
                        else next.add(conv.id);
                        return next;
                      });
                    }}
                    className="w-4 h-4 rounded accent-primary-500"
                    onClick={(e) => e.stopPropagation()}
                  />
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    {conv.labelEmoji && <span className="text-sm">{conv.labelEmoji}</span>}
                    <p className="text-sm font-medium text-dark-text dark:text-dark-text text-light-text truncate">
                      {conv.title || t('conversations.untitled', 'Untitled')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-dark-text-secondary">
                      {conv.messageCount} {t('conversations.msgs', 'msgs')}
                    </span>
                    {conv.lastMessageAt && (
                      <span className="text-xs text-dark-text-secondary">
                        · {formatDate(conv.lastMessageAt)}
                      </span>
                    )}
                    {conv.label && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-dark-card/50 text-dark-text-secondary">
                        {conv.label}
                      </span>
                    )}
                  </div>
                </div>

                {!batchMode && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setLabelingId(labelingId === conv.id ? null : conv.id);
                      }}
                      className="p-1 rounded hover:bg-dark-card/50 transition-colors"
                    >
                      <TagIcon className="w-4 h-4 text-dark-text-secondary" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleArchive(conv.id);
                      }}
                      className="p-1 rounded hover:bg-dark-card/50 transition-colors"
                    >
                      <ArchiveBoxIcon className="w-4 h-4 text-dark-text-secondary" />
                    </button>
                  </div>
                )}

                {/* Label picker popover */}
                {labelingId === conv.id && (
                  <div
                    className="absolute right-16 bg-dark-bg border border-dark-border rounded-lg shadow-xl p-2 z-50 grid grid-cols-4 gap-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {LABELS.map((l) => (
                      <button
                        key={l.name}
                        onClick={() => handleSetLabel(conv.id, l.name, l.emoji)}
                        className="p-1.5 rounded hover:bg-dark-card/50 text-center"
                        title={l.name}
                      >
                        <span className="text-lg">{l.emoji}</span>
                      </button>
                    ))}
                    {conv.label && (
                      <button
                        onClick={() => handleClearLabel(conv.id)}
                        className="p-1.5 rounded hover:bg-red-900/30 text-center col-span-4 text-xs text-red-400"
                      >
                        {t('conversations.clearLabel', 'Clear')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-dark-border/30 text-xs text-dark-text-secondary">
          {conversations.length} {t('conversations.count', 'conversations')}
        </div>
      </div>
    </>
  );
};

export default ConversationDashboard;
